import {
  type ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FieldProps } from '@rjsf/utils';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

/**
 * Generic row data for horizontally rendered array inputs.
 */
type RowData = Record<string, string | undefined>;

type PropertySchema = {
  title?: string;
  format?: string;
  description?: string;
  enum?: string[];
  pattern?: string;
  errorMessage?: string;
};

type FieldStatus = 'default' | 'error';

const REQUIRED_ERROR_MESSAGE = 'Error. This is a required field.';

/**
 * Renders an array field as horizontal rows of generic inputs.
 */
export const HorizontalInputGroupArrayCard = ({
  onChange,
  formData,
  schema,
  idSchema,
  disabled,
  readonly,
  uiSchema,
}: FieldProps<RowData[]>) => {
  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'dark' ? '#0D0D0D' : '#F4F4F4';

  const itemsSchema = useMemo(() => (schema as any)?.items ?? {}, [schema]);
  const propsSchema = useMemo(
    () =>
      (itemsSchema?.properties ?? {}) as Record<
        string,
        PropertySchema & { const?: string; default?: string }
      >,
    [itemsSchema],
  );
  const uiItems = useMemo(() => (uiSchema as any)?.items ?? {}, [uiSchema]);
  const order =
    (uiItems?.['ui:order'] as string[] | undefined) ??
    ((uiSchema as any)?.['ui:options']?.fields as string[] | undefined);
  const addButtonLabel =
    ((uiSchema as any)?.['ui:options']?.addButtonLabel as string | undefined) ??
    'Add item';

  const minItems = typeof schema.minItems === 'number' ? schema.minItems : 0;
  const maxItems =
    typeof schema.maxItems === 'number' ? schema.maxItems : undefined;

  const constValues: Record<string, string> = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(propsSchema)
          .filter(k => (propsSchema[k] as any)?.const !== undefined)
          .map(k => [
            k,
            String(
              (propsSchema[k] as any)?.const ??
                (propsSchema[k] as any)?.default ??
                '',
            ),
          ]),
      ),
    [propsSchema],
  );

  const visibleKeys = useMemo(() => {
    const allKeys = Object.keys(propsSchema).filter(k => {
      const uiWidget = (uiItems as any)?.[k]?.['ui:widget'];
      const hasConst = (propsSchema[k] as any)?.const !== undefined;
      return uiWidget !== 'hidden' && !hasConst;
    });

    if (!order?.length) {
      return allKeys;
    }

    return [
      ...order.filter(k => allKeys.includes(k)),
      ...allKeys.filter(k => !order.includes(k)),
    ];
  }, [order, propsSchema, uiItems]);

  const requiredKeys = ((itemsSchema?.required ?? []) as string[]).filter(k =>
    visibleKeys.includes(k),
  );

  const createEmptyRow = () =>
    visibleKeys.reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {} as RowData);

  const normalizeRows = (rows?: RowData[]) => {
    if (Array.isArray(rows) && rows.length > 0) {
      return rows.map(row => {
        const normalized = createEmptyRow();
        visibleKeys.forEach(key => {
          normalized[key] = typeof row?.[key] === 'string' ? row[key] : '';
        });
        return normalized;
      });
    }

    const initialLength = Math.max(minItems, 1);
    return Array.from({ length: initialLength }, () => createEmptyRow());
  };

  const [localValues, setLocalValues] = useState<RowData[]>(() =>
    normalizeRows(Array.isArray(formData) ? formData : undefined),
  );
  const [fieldStatus, setFieldStatus] = useState<Record<string, FieldStatus>>(
    {},
  );
  const [fieldHelpers, setFieldHelpers] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );

  useEffect(() => {
    const normalizedFormData = normalizeRows(
      Array.isArray(formData) ? formData : undefined,
    );

    if (JSON.stringify(normalizedFormData) !== JSON.stringify(localValues)) {
      setLocalValues(normalizedFormData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    onChange(localValues.map(row => ({ ...constValues, ...row })));
  }, [constValues, localValues, onChange]);

  function setFieldState(
    rowIndex: number,
    key: string,
    status: FieldStatus,
    text: string,
  ) {
    const fieldKey = `${rowIndex}:${key}`;
    setFieldStatus(prev => ({ ...prev, [fieldKey]: status }));
    setFieldHelpers(prev => ({ ...prev, [fieldKey]: text }));
  }

  function matchesPattern(value: string, pattern?: string) {
    if (!pattern) {
      return true;
    }

    try {
      return new RegExp(pattern).test(value);
    } catch {
      return false;
    }
  }

  function validateField(rowIndex: number, key: string, value: string) {
    const property = propsSchema[key];
    const isRequired = requiredKeys.includes(key);
    const fieldKey = `${rowIndex}:${key}`;

    if (isRequired && value.trim().length === 0) {
      setFieldState(rowIndex, key, 'error', REQUIRED_ERROR_MESSAGE);
      return false;
    }

    if (value.trim().length > 0 && !matchesPattern(value, property?.pattern)) {
      setFieldState(
        rowIndex,
        key,
        'error',
        property?.errorMessage ??
          'Error. Inputted text is in an invalid format.',
      );
      return false;
    }

    setFieldState(rowIndex, key, 'default', property?.description ?? '');
    setTouchedFields(prev => ({ ...prev, [fieldKey]: true }));
    return true;
  }

  const handleChange =
    (rowIndex: number, key: string) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = [...localValues];
      const row = { ...(next[rowIndex] ?? {}) };
      row[key] = event.target.value;
      next[rowIndex] = row;
      setLocalValues(next);

      const fieldKey = `${rowIndex}:${key}`;
      if (touchedFields[fieldKey] || fieldStatus[fieldKey] === 'error') {
        validateField(rowIndex, key, event.target.value);
      } else {
        setFieldState(
          rowIndex,
          key,
          'default',
          propsSchema[key]?.description ?? '',
        );
      }
    };

  const handleBlur = (rowIndex: number, key: string) => {
    const fieldKey = `${rowIndex}:${key}`;
    setTouchedFields(prev => ({ ...prev, [fieldKey]: true }));
    validateField(rowIndex, key, localValues[rowIndex]?.[key] ?? '');
  };

  function handleAdd() {
    setLocalValues(prev => [...prev, createEmptyRow()]);
  }

  function handleRemove(rowIndex: number) {
    setLocalValues(prev => prev.filter((_, index) => index !== rowIndex));
    setFieldStatus({});
    setFieldHelpers({});
    setTouchedFields({});
  }

  const canAdd =
    !disabled &&
    !readonly &&
    (maxItems === undefined || localValues.length < maxItems);

  return (
    <Box
      sx={{
        backgroundColor,
        padding: 2,
        borderRadius: 1,
      }}
    >
      {schema.title && (
        <Typography variant="h6" sx={{ marginBottom: 1, marginTop: 0 }}>
          {schema.title}
        </Typography>
      )}

      {schema.description && (
        <Typography variant="body2" sx={{ marginBottom: 1 }}>
          {schema.description}
        </Typography>
      )}

      {(schema.title || schema.description) && (
        <Divider sx={{ marginBottom: 2, marginTop: 1 }} />
      )}

      <Stack spacing={2}>
        {localValues.map((row, rowIndex) => {
          const canRemove =
            !disabled &&
            !readonly &&
            localValues.length > Math.max(minItems, 1);

          return (
            <Fragment key={`${idSchema.$id}_row_${rowIndex}`}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                sx={{ width: '100%' }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  sx={{ flex: 1 }}
                >
                  {visibleKeys.map(key => {
                    const property = propsSchema[key];
                    const fieldKey = `${rowIndex}:${key}`;
                    const status = fieldStatus[fieldKey] ?? 'default';
                    const placeholder =
                      uiItems?.[key]?.['ui:placeholder'] ??
                      property?.title ??
                      key;
                    const enumValues = property?.enum;

                    return (
                      <TextField
                        key={`${idSchema.$id}_${rowIndex}_${key}`}
                        id={`${idSchema.$id}_${rowIndex}_${key}`}
                        label={property?.title ?? key}
                        select={
                          Array.isArray(enumValues) && enumValues.length > 0
                        }
                        value={row?.[key] ?? ''}
                        onChange={handleChange(rowIndex, key)}
                        onBlur={() => handleBlur(rowIndex, key)}
                        placeholder={placeholder}
                        required={requiredKeys.includes(key)}
                        disabled={disabled || readonly}
                        type={property?.format === 'email' ? 'email' : 'text'}
                        variant="outlined"
                        fullWidth
                        error={status === 'error'}
                        helperText={
                          fieldHelpers[fieldKey] ?? property?.description ?? ''
                        }
                        SelectProps={
                          Array.isArray(enumValues) && enumValues.length > 0
                            ? { displayEmpty: true }
                            : undefined
                        }
                        sx={{ flex: 1 }}
                      >
                        {Array.isArray(enumValues) &&
                          enumValues.length > 0 && [
                            <MenuItem
                              key={`${key}_placeholder`}
                              value=""
                              disabled
                            >
                              {placeholder}
                            </MenuItem>,
                            ...enumValues.map(enumValue => (
                              <MenuItem key={enumValue} value={enumValue}>
                                {enumValue}
                              </MenuItem>
                            )),
                          ]}
                      </TextField>
                    );
                  })}
                </Stack>

                <IconButton
                  aria-label="Remove"
                  onClick={() => handleRemove(rowIndex)}
                  disabled={!canRemove}
                  size="medium"
                  sx={{ mt: { xs: 0, md: 0.5 } }}
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Stack>

              {rowIndex < localValues.length - 1 && <Divider sx={{ my: 1 }} />}
            </Fragment>
          );
        })}
      </Stack>

      {(minItems !== 1 || maxItems !== 1) && (
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={handleAdd}
            disabled={!canAdd}
            size="small"
            startIcon={<AddCircleOutlineIcon />}
          >
            {addButtonLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
};
